import { useCallback, useEffect, useState } from "react";
import "../styles/addToHomeScreen.css";

const STORAGE_KEY = "a2hs-dismissed";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 4000;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

function isMobileDevice() {
  return isIOS() || isAndroid();
}

function shouldForceShow() {
  try {
    return new URLSearchParams(window.location.search).get("a2hs") === "1";
  } catch {
    return false;
  }
}

function wasRecentlyDismissed() {
  if (shouldForceShow()) return false;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function AddToHomeScreen() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const platform = isIOS() ? "ios" : "android";

  useEffect(() => {
    const force = shouldForceShow();
    if (!isMobileDevice()) return undefined;
    if (!force && (isStandalone() || wasRecentlyDismissed())) return undefined;

    let timer;
    let removeListener;

    if (isIOS()) {
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    } else if (isAndroid()) {
      const showAfterDelay = () => {
        timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      };

      const onBeforeInstall = (event) => {
        event.preventDefault();
        setDeferredPrompt(event);
        showAfterDelay();
      };

      window.addEventListener("beforeinstallprompt", onBeforeInstall);
      removeListener = () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);

      // HTTP / LAN dev won't fire beforeinstallprompt — still show manual steps.
      showAfterDelay();
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (removeListener) removeListener();
    };
  }, []);

  const dismiss = useCallback(() => {
    if (!shouldForceShow()) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        // ignore storage errors
      }
    }
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  }, [deferredPrompt, dismiss]);

  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div className="a2hs-overlay" role="dialog" aria-modal="true" aria-label="Add CollectDiv to your home screen">
      <div className="a2hs-backdrop" onClick={dismiss} aria-hidden />
      <div className="a2hs-panel">
        <button type="button" className="a2hs-close" onClick={dismiss} aria-label="Close">
          ×
        </button>

        <div className="a2hs-icon-wrap">
          <img src="/apple-touch-icon.png" alt="" className="a2hs-app-icon" width={64} height={64} />
        </div>

        <h2 className="a2hs-title">Add CollectDiv to your home screen</h2>
        <p className="a2hs-subtitle">
          Install the app for quick access to your collection, wishlist, and shop.
        </p>

        {platform === "ios" ? (
          <ol className="a2hs-steps">
            <li>
              Tap the <strong>Share</strong> button{" "}
              <span className="a2hs-share-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v12M8 7l4-4 4 4M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>
            </li>
            <li>
              Tap <strong>Add</strong> in the top right
            </li>
          </ol>
        ) : deferredPrompt ? (
          <p className="a2hs-android-hint">
            Install CollectDiv on your device for a full-screen app experience.
          </p>
        ) : (
          <ol className="a2hs-steps">
            <li>
              Tap the <strong>menu</strong> button{" "}
              <span className="a2hs-menu-icon" aria-hidden="true">⋮</span> in Chrome
            </li>
            <li>
              Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>
            </li>
          </ol>
        )}

        <div className="a2hs-actions">
          {platform === "android" && deferredPrompt && (
            <button type="button" className="a2hs-primary" onClick={install}>
              Install app
            </button>
          )}
          <button type="button" className="a2hs-secondary" onClick={dismiss}>
            {platform === "android" && deferredPrompt ? "Not now" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
